import { Box } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import type { TableCellStyle } from '../../template/Table';
import { handleCellPaste, handleInlineFormatShortcut } from './tableCellEvents';
import { sanitizeCellHtml } from './tableModel';

/** Background + weight per cell style — shared with the row renderer. */
export const CELL_STYLE_SX: Record<TableCellStyle, { bgcolor: string; fontWeight: number }> = {
  header: { bgcolor: '#eef0ec', fontWeight: 700 },
  subHeader: { bgcolor: '#f6f7f4', fontWeight: 600 },
  normal: { bgcolor: 'transparent', fontWeight: 400 },
};

interface TableCellEditorProps {
  html: string;
  styleKind: TableCellStyle;
  selected: boolean;
  ariaLabel: string;
  onCommit: (html: string) => void;
  onFocusCell: () => void;
  onSelectStart: () => void;
  onSelectExtend: () => void;
  onKeyNav: (event: React.KeyboardEvent) => void;
  onPasteTable: (html: string, text: string) => boolean;
  onPasteImage: (blob: Blob) => void;
}

/** One editable table cell. Plain content-editable; sanitised to inline-only on blur. */
export default function TableCellEditor({
  html,
  styleKind,
  selected,
  ariaLabel,
  onCommit,
  onFocusCell,
  onSelectStart,
  onSelectExtend,
  onKeyNav,
  onPasteTable,
  onPasteImage,
}: TableCellEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const styled = styleKind !== 'normal';

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  return (
    <Box
      component="div"
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      contentEditable
      suppressContentEditableWarning
      onMouseDown={onSelectStart}
      onMouseEnter={(event) => {
        if (event.buttons === 1) onSelectExtend();
      }}
      onFocus={onFocusCell}
      onBlur={() => {
        const el = ref.current;
        if (!el) return;
        const clean = sanitizeCellHtml(el.innerHTML);
        if (clean !== html) onCommit(clean);
      }}
      onKeyDown={(event) => {
        if (handleInlineFormatShortcut(event, styled)) return;
        onKeyNav(event);
      }}
      onPaste={(event) => handleCellPaste(event, { onPasteImage, onPasteTable })}
      sx={{
        minWidth: 96,
        minHeight: '1.5em',
        px: 1,
        py: 0.5,
        outline: 'none',
        fontFamily: 'Inter, sans-serif',
        fontSize: '0.9rem',
        fontWeight: CELL_STYLE_SX[styleKind].fontWeight,
        bgcolor: selected ? 'action.selected' : 'transparent',
        '&:focus': { boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}` },
      }}
    />
  );
}
